/**
 * Login & Register page — email/password + OAuth (Google, GitHub).
 *
 * Why a single page instead of two?
 *   - Users frequently switch between "I have an account" and "I need one"
 *   - A single page avoids route navigation during a frustration-prone flow
 *   - The forms share the same layout, reducing code duplication
 *
 * OAuth flow:
 *   The backend redirects to this page with a single-use ?code=... The
 *   useEffect on mount exchanges that code server-side (POST
 *   /auth/oauth/exchange) for the token pair, then stores the tokens and
 *   redirects to the dashboard. The code approach keeps the 7-day refresh
 *   token out of the URL fragment, browser history, and third-party JS (M-5).
 *
 * After successful auth, redirects to the page the user was trying to reach
 * (via React Router's `state.from` pattern) or to the dashboard.
 */

import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Tab,
  Tabs,
  TextField,
  FormControlLabel,
  Typography,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import GitHubIcon from "@mui/icons-material/GitHub";
import { useAuth } from "../hooks/useAuth";
import {
  beginOAuthRegistration,
  ensureDeviceBinding,
  exchangeOAuthCode,
  isPolicyConsentRequiredError,
  resendVerificationEmail,
  verifyEmail,
} from "../api/client";
import { toUserSafeErrorMessage } from "../utils/errorMessage";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

// GitHub OAuth: GITHUB_CLIENT_ID/SECRET are provisioned in Railway and the
// backend flow is verified (emails API → primary+verified gate, M-1), so the
// button is enabled. Requires the OAuth App's callback URL to be
// https://api.career-forge.org/auth/oauth/github/callback.
const GITHUB_OAUTH_ENABLED = true;

/**
 * Accept a post-login destination from the untrusted `?redirect=` query param
 * ONLY if it resolves to a same-origin internal path — otherwise
 * `/login?redirect=...` is an open redirect. Router `state.from` (set by app
 * code) is trusted and bypasses this; the query param must be validated.
 *
 * We normalise through the browser's own URL parser and compare origins rather
 * than blocklisting characters. A blocklist is defeated by `/<TAB>/evil.com`
 * (`%2F%09%2Fevil.com`): the tab passes a naive `startsWith("//")` check, but
 * the browser strips it during navigation, react-router's `pushState` throws a
 * cross-origin `SecurityError`, and react-router then falls back to a hard
 * `location.assign` off-origin. `new URL` strips the same control chars BEFORE
 * the origin check, so the stripping trick cannot slip past.
 */
export function safeInternalPath(raw: string | null | undefined): string | null {
  if (!raw || !raw.startsWith("/")) return null;
  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return url.pathname + url.search + url.hash;
  } catch {
    return null;
  }
}

export default function LoginPage() {
  const { t } = useTranslation("pages-products");
  const [tab, setTab] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [registrationNotice, setRegistrationNotice] = useState<string | null>(null);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [acceptedPolicies, setAcceptedPolicies] = useState(false);
  const [policyConsentRequired, setPolicyConsentRequired] = useState(false);
  const [loading, setLoading] = useState(false);
  const submitInFlightRef = useRef(false);

  const { login, register, startExternalAuthCompletion } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Where to redirect after auth. Priority: trusted router state.from (set by
  // ProtectedRoute), then a validated ?redirect= query param (e.g. readiness
  // session-expiry sends the user here with the intended route), then dashboard.
  const from =
    (location.state as { from?: string })?.from ||
    safeInternalPath(new URLSearchParams(location.search).get("redirect")) ||
    "/";

  // The redemption effect below must run EXACTLY ONCE on mount and survive
  // unrelated re-renders. It closes over values that change identity across
  // renders — most dangerously `t`, whose identity flips when its i18n
  // namespace finishes loading. Holding them in refs lets the effect depend on
  // nothing, so a spurious re-render can neither restart it nor tear it down
  // and abort an in-flight verify, while keeping exhaustive-deps satisfied.
  const fromRef = useRef(from);
  const navigateRef = useRef(navigate);
  const queryClientRef = useRef(queryClient);
  const tRef = useRef(t);
  const startExternalAuthCompletionRef = useRef(startExternalAuthCompletion);
  fromRef.current = from;
  navigateRef.current = navigate;
  queryClientRef.current = queryClient;
  tRef.current = t;
  startExternalAuthCompletionRef.current = startExternalAuthCompletion;
  const redemptionStartedRef = useRef(false);

  // ---------------------------------------------------------------------------
  // Handle OAuth redirect — the backend redirects with a single-use ?code=...
  // which we exchange server-side for the token pair (M-5). This keeps the
  // 7-day refresh token out of the URL fragment, browser history, and any
  // third-party JS on this page. The same effect redeems an email
  // ?verify_token=... hand-off.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const verificationToken = params.get("verify_token");
    if (!code && !verificationToken) return;

    // Redeem exactly once. A React re-render (an i18n `t` identity flip, a
    // parent state update, StrictMode's dev double-mount) must neither restart
    // this flow nor cancel a healthy in-flight request — the P0 email-verify
    // outage was exactly that: the effect re-ran on a `t` change and its
    // cleanup aborted the pending /auth/verify-email before it was ever sent.
    if (redemptionStartedRef.current) return;
    redemptionStartedRef.current = true;

    // The auth context owns the entire credential transition. Capturing this
    // handle before redeeming makes a later login/logout/account switch abort
    // the request and reject its late result without LoginPage touching tokens.
    const completion = startExternalAuthCompletionRef.current();
    (async () => {
      try {
        if (code) {
          // OAuth: the device binding must exist BEFORE the code exchange mints
          // a refresh family, so this path legitimately blocks on bootstrap.
          await ensureDeviceBinding(completion.signal);
        } else {
          // Email verification: the server mints the device binding during
          // activation, so verify MUST NOT block on the client bootstrap
          // coordination. That single-flight can hang or never settle (in-app
          // mail webviews without Web Locks / IndexedDB, a fresh private
          // window), and awaiting it stranded brand-new accounts as
          // unverifiable. Kick bootstrap off fire-and-forget so an already-bound
          // browser still refreshes its cookie, then verify regardless of its
          // fate. A rejection is swallowed; the server-side mint is the proof.
          void ensureDeviceBinding(completion.signal).catch(() => {
            /* server-side mint on verify is the fallback */
          });
        }
        const tokens = code
          ? await exchangeOAuthCode(code, completion.signal)
          : await verifyEmail(verificationToken!, completion.signal);
        // Do not short-circuit on local effect cleanup: accept() must see the
        // late response so the context can contain a Set-Cookie it may carry.
        await completion.accept(tokens, () => {
          // Clear the code only for the accepted flow. A stale completion may
          // never change navigation/history or the displayed account.
          window.history.replaceState(null, "", window.location.pathname);
          // Drop cached (logged-out) data so entitlement-gated content refetches
          // with the new token — otherwise a locked module stays locked until reload.
          queryClientRef.current.invalidateQueries();
          navigateRef.current(fromRef.current, { replace: true });
        });
      } catch {
        // A rejected/aborted request may still have reached the backend and
        // installed a cookie. The context finalizer contains that possibility
        // and tells us whether this exact effect remains authoritative. Never
        // alter history or visible error state for a superseded flow.
        const finalization = await completion.finalizeFailure(true);
        if (!finalization.current || !finalization.safeToPresent) return;
        window.history.replaceState(null, "", window.location.pathname);
        setError(
          code
            ? tRef.current("LoginPage.oauthExpiredError")
            : tRef.current("LoginPage.emailVerificationExpiredError"),
        );
      }
    })();

    // No teardown that cancels the redemption. A genuine account switch
    // (sign-in, sign-out, or a fresh user re-load) calls beginAuthWork, which
    // already aborts this
    // completion's signal and advances the flow nonce — so a real supersede is
    // still contained by the completion's own fences, without a benign
    // re-render or unmount aborting a healthy verify/exchange in flight.
  }, []);

  // ---------------------------------------------------------------------------
  // Email/password auth
  // ---------------------------------------------------------------------------
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitInFlightRef.current) return;
    setError(null);

    if (!email || !password) {
      setError(t("LoginPage.emailPasswordRequiredError"));
      return;
    }

    if (tab === "register") {
      if (password.length < 8) {
        setError(t("LoginPage.passwordTooShortError"));
        return;
      }
      if (password !== confirmPassword) {
        setError(t("LoginPage.passwordsDoNotMatchError"));
        return;
      }
      if (!acceptedPolicies) {
        setError(t("LoginPage.policyAcceptanceRequiredError"));
        return;
      }
    }
    if (tab === "login" && policyConsentRequired && !acceptedPolicies) {
      setError(t("LoginPage.policyAcceptanceRequiredError"));
      return;
    }

    // State updates are asynchronous. This ref closes the interval before the
    // disabled button renders, so rapid duplicate submits issue one request.
    submitInFlightRef.current = true;
    setLoading(true);
    try {
      if (tab === "login") {
        await login(email, password, policyConsentRequired && acceptedPolicies);
      } else {
        const result = await register(email, password, displayName || undefined);
        if (result.emailVerificationRequired) {
          setRegistrationNotice(t("LoginPage.emailVerificationSentNotice"));
          return;
        }
      }
      // Refetch entitlement-gated content with the new token (else cached
      // logged-out data keeps a module locked until a manual refresh).
      queryClient.invalidateQueries();
      navigate(from, { replace: true });
    } catch (err: unknown) {
      if (tab === "login" && isPolicyConsentRequiredError(err)) {
        setPolicyConsentRequired(true);
        setError(t("LoginPage.policyAcceptanceRequiredError"));
        return;
      }
      setError(
        toUserSafeErrorMessage(err, t("LoginPage.genericErrorMessage")),
      );
    } finally {
      submitInFlightRef.current = false;
      setLoading(false);
    }
  };

  const resetForm = () => {
    setError(null);
    setRegistrationNotice(null);
    setPassword("");
    setConfirmPassword("");
    setAcceptedPolicies(false);
    setPolicyConsentRequired(false);
  };

  const handleResendVerification = async () => {
    if (!email) {
      setError(t("LoginPage.emailPasswordRequiredError"));
      return;
    }
    setError(null);
    setResendingVerification(true);
    try {
      await resendVerificationEmail(email);
      setRegistrationNotice(t("LoginPage.emailVerificationSentNotice"));
    } catch (err: unknown) {
      setError(toUserSafeErrorMessage(err, t("LoginPage.genericErrorMessage")));
    } finally {
      setResendingVerification(false);
    }
  };

  // ---------------------------------------------------------------------------
  // OAuth handlers — redirect to backend which redirects to provider
  // ---------------------------------------------------------------------------
  const handleGoogleLogin = async () => {
    if (tab === "register" && !acceptedPolicies) {
      setError(t("LoginPage.policyAcceptanceRequiredError"));
      return;
    }
    setError(null);
    try {
      await ensureDeviceBinding();
      if (tab === "register") await beginOAuthRegistration();
      window.location.href = `${API_BASE}/auth/oauth/google`;
    } catch (err: unknown) {
      setError(
        toUserSafeErrorMessage(err, t("LoginPage.genericErrorMessage")),
      );
    }
  };

  const handleGithubLogin = async () => {
    if (tab === "register" && !acceptedPolicies) {
      setError(t("LoginPage.policyAcceptanceRequiredError"));
      return;
    }
    setError(null);
    try {
      await ensureDeviceBinding();
      if (tab === "register") await beginOAuthRegistration();
      window.location.href = `${API_BASE}/auth/oauth/github`;
    } catch (err: unknown) {
      setError(
        toUserSafeErrorMessage(err, t("LoginPage.genericErrorMessage")),
      );
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        px: 2,
      }}
    >
      {/* Branding */}
      <Stack alignItems="center" sx={{ mb: 4 }}>
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/favicon_1.png`}
          alt={t("LoginPage.logoAlt")}
          sx={{ width: 72, height: 72, objectFit: "contain", mb: 2 }}
        />
        <Typography
          variant="h4"
          component="h1"
          fontWeight={500}
          color="text.primary"
          gutterBottom
          sx={{ letterSpacing: "-0.02em" }}
        >
          {t("LoginPage.brandName")}
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ maxWidth: 320 }}>
          {t("LoginPage.tagline")}
        </Typography>
      </Stack>

      {/* Auth card */}
      <Card sx={{ width: "100%", maxWidth: 440 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Tabs
            value={tab}
            onChange={(_, v) => {
              setTab(v);
              resetForm();
            }}
            variant="fullWidth"
            sx={{ mb: 3 }}
          >
            <Tab label={t("LoginPage.signInTab")} value="login" />
            <Tab label={t("LoginPage.createAccountTab")} value="register" />
          </Tabs>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {registrationNotice && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {registrationNotice}
            </Alert>
          )}

          {/* OAuth buttons */}
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<GoogleIcon />}
              onClick={handleGoogleLogin}
              sx={{
                py: 1.2,
                textTransform: "none",
                borderColor: "divider",
                color: "text.primary",
                "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
              }}
            >
              {t("LoginPage.continueWithGoogleButton")}
            </Button>
            {GITHUB_OAUTH_ENABLED && (
              <Button
                variant="outlined"
                fullWidth
                startIcon={<GitHubIcon />}
                onClick={handleGithubLogin}
                sx={{
                  py: 1.2,
                  textTransform: "none",
                  borderColor: "divider",
                  color: "text.primary",
                  "&:hover": { borderColor: "text.primary", bgcolor: "action.hover" },
                }}
              >
                {t("LoginPage.continueWithGithubButton")}
              </Button>
            )}
          </Stack>

          <Divider sx={{ my: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {t("LoginPage.orDivider")}
            </Typography>
          </Divider>

          {/* Email/password form */}
          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              {tab === "register" && (
                <TextField
                  label={t("LoginPage.displayNameLabel")}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  fullWidth
                  autoComplete="name"
                  placeholder={t("LoginPage.displayNamePlaceholder")}
                />
              )}

              <TextField
                label={t("LoginPage.emailLabel")}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
                autoComplete="email"
              />

              <TextField
                label={t("LoginPage.passwordLabel")}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                fullWidth
                autoComplete={
                  tab === "login" ? "current-password" : "new-password"
                }
                helperText={
                  tab === "register" ? t("LoginPage.passwordMinLengthHint") : undefined
                }
              />

              {(tab === "register" || policyConsentRequired) && (
                <>
                  {tab === "register" && (
                    <TextField
                      label={t("LoginPage.confirmPasswordLabel")}
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      fullWidth
                      autoComplete="new-password"
                    />
                  )}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={acceptedPolicies}
                        onChange={(e) => setAcceptedPolicies(e.target.checked)}
                        required={tab === "register" || policyConsentRequired}
                      />
                    }
                    label={
                      <Typography variant="body2" color="text.secondary">
                        {t("LoginPage.policyAcceptancePrefix")}{" "}
                        <Link href="/terms" target="_blank" rel="noopener noreferrer">
                          {t("LoginPage.termsLink")}
                        </Link>{" "}
                        {t("LoginPage.policyAnd")}{" "}
                        <Link href="/privacy" target="_blank" rel="noopener noreferrer">
                          {t("LoginPage.privacyLink")}
                        </Link>.
                      </Typography>
                    }
                  />
                </>
              )}

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
                sx={{ py: 1.5, mt: 1 }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : tab === "login" ? (
                  t("LoginPage.signInButton")
                ) : (
                  t("LoginPage.createAccountButton")
                )}
              </Button>

              {tab === "login" && (
                <Box display="flex" justifyContent="space-between" gap={1}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    disabled={resendingVerification}
                    onClick={handleResendVerification}
                  >
                    {t("LoginPage.resendVerificationLink")}
                  </Link>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => navigate("/forgot-password")}
                  >
                    {t("LoginPage.forgotPasswordLink")}
                  </Link>
                </Box>
              )}
            </Stack>
          </form>

          <Divider sx={{ my: 3 }} />

          <Typography variant="body2" color="text.secondary" textAlign="center">
            {tab === "login" ? (
              <Trans
                i18nKey="LoginPage.noAccountPrompt"
                t={t}
                components={[
                  <Link
                    component="button"
                    variant="body2"
                    key="link0"
                    onClick={() => {
                      setTab("register");
                      resetForm();
                    }}
                  />,
                ]}
              />
            ) : (
              <Trans
                i18nKey="LoginPage.hasAccountPrompt"
                t={t}
                components={[
                  <Link
                    component="button"
                    variant="body2"
                    key="link0"
                    onClick={() => {
                      setTab("login");
                      resetForm();
                    }}
                  />,
                ]}
              />
            )}
          </Typography>
        </CardContent>
      </Card>

      {/* Skip auth link for browsing */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 2, textAlign: "center" }}
      >
        <Link
          component="button"
          variant="body2"
          color="text.secondary"
          onClick={() => navigate("/")}
          sx={{ textDecoration: "underline" }}
        >
          {t("LoginPage.continueWithoutSigningInLink")}
        </Link>
      </Typography>
    </Box>
  );
}
